<template>
  <div v-if="sdnData.length">
    <v-navigation-drawer
      ref="customiseDemandsAndNeeds"
      v-model="valueLocal"
      app
      right
      temporary
      class="v-navigation-drawer--width-max"
    >
      <div class="drawer-container d-flex flex-column justify-space-between">
        <div
          class="drawer-header d-flex align-center justify-space-between pa-5"
        >
          <h3 class="font-28 font-weight-bold">Create SDN</h3>
          <v-btn
            icon
            aria-label="Close"
            @click="valueLocal = !valueLocal"
            class="btn-close"
          >
            <v-icon size="32">mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="drawer-main flex-grow-1 pa-1 pa-xl-3">
          <BaseAlert
            v-if="isRequestError"
            ref="error"
            color="var(--red-100)"
            class="mt-5 mx-5 mb-0 pa-4 border-sm border-solid border-error"
          >
            <div class="d-flex flex-no-wrap error-message">
              <div class="mr-3">
                <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
              </div>
              <div class="primary--text">
                <span class="font-weight-bold">Sorry!</span>&nbsp;Something went
                wrong. Please try again.
              </div>
            </div>
          </BaseAlert>

          <template>
            <v-container v-if="showToggles">
              <!-- Toggles Section -->
              <div class="pa-5">
                <!-- Info Message -->
                <BaseAlert
                  color="var(--blue-100)"
                  class="pa-4 border-sm border-solid border-info"
                >
                  <div class="d-flex flex-no-wrap">
                    <div class="mr-3">
                      <v-icon color="var(--blue-500)">mdi-information</v-icon>
                    </div>
                    <div class="primary--text">
                      SDN creation is a two stage process. Firstly, select any
                      statements you want to include from the list below.
                      <br />
                      In the next stage you will be able to edit these
                      statements as well as enter your own text to complete the
                      SDN.
                    </div>
                  </div>
                </BaseAlert>

                <!-- The Questions -->
                <div
                  v-for="(sdnSection, index) in sdnTemplate"
                  :key="sdnSection.interfaceKey"
                >
                  <v-container
                    class="mb-3"
                    v-if="sdnSection.sdnItems.length > 0"
                  >
                    <div
                      class="mb-3 px-3"
                      :class="{ 'mt-10': index > 0, 'mt-5': index === 0 }"
                    >
                      <h4 class="font-20 mb-2 pr-md-16">
                        {{ sdnSection.title }}
                      </h4>
                      <p v-if="sdnSection.description">
                        {{ sdnSection.description }}
                      </p>
                    </div>

                    <div class="mb-3">
                      <div v-for="item in sdnSection.sdnItems" :key="item.text">
                        <div class="d-flex flex-column flex-lg-row flex-wrap">
                          <div
                            class="d-flex align-center col-12 col-lg-7 pr-md-16"
                          >
                            {{ item.text }}
                          </div>
                          <div class="d-flex align-center col-12 col-lg-5">
                            <BaseToggleButton
                              :height="
                                $vuetify.breakpoint.lgAndUp ? '48' : '60'
                              "
                              v-model="item.value"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-container>
                </div>
              </div>

              <!-- End of Question Section -->
            </v-container>

            <v-container v-if="!showToggles">
              <!-- Editable Textarea Section -->
              <div class="pa-5">
                <!-- Back Button -->
                <div class="row">
                  <div class="col-12">
                    <BaseButton
                      text="Back"
                      flat
                      depressed
                      ripple
                      class="font-weight-bold d-flex ml-n5 mb-2"
                      icon="mdi-chevron-left"
                      color="#106175"
                      @click="showToggles = true"
                    />
                  </div>
                </div>

                <!-- The Questions -->
                <div
                  v-for="(sdnSection, index) in sdnTemplate"
                  :key="sdnSection.interfaceKey"
                >
                  <div class="row">
                    <div class="col-12 pb-2">
                      <h4 class="mx-3 font-20">
                        {{ sdnSection.shortTitle ?? sdnSection.title }}
                      </h4>
                    </div>
                  </div>
                  <div class="row">
                    <div
                      class="col-12"
                      :class="{ 'py-0 mb-9': index != sdnTemplate.length - 1 }"
                    >
                      <BaseTextarea
                        class="mx-3"
                        v-model="sdnSection.text"
                        :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                        validationMode="passive"
                        hideValidationIcon
                        hideDetails
                      />
                    </div>
                  </div>

                  <BaseAlert
                    color="var(--red-100)"
                    class="mt-6 mb-0 ml-3 mr-4 pa-4 border-sm border-solid border-error"
                    v-if="invalid && index == sdnTemplate.length - 1"
                  >
                    <div class="d-flex flex-no-wrap">
                      <div class="mr-4 v-messages error--text">
                        <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
                      </div>
                      <div>
                        <span class="font-weight-bold">Sorry!</span>
                        There must be at least one completed statement to create
                        a statement of demands and needs.
                      </div>
                    </div>
                  </BaseAlert>
                </div>
              </div>
              <!-- End of Editable Textarea Section -->
            </v-container>
          </template>
        </div>

        <div class="drawer-footer d-flex flex-column pa-5">
          <BaseButton
            v-if="showToggles"
            text="PROCEED"
            height="68"
            block
            color="psorange"
            class="font-weight-bold font-20 rounded-pill black--text"
            @click="populateSelectedSDNs(sdnTemplate)"
            type="submit"
          />
          <BaseButton
            v-else
            text="CREATE SDN"
            height="68"
            block
            color="psorange"
            class="font-weight-bold font-20 rounded-pill black--text submit-btn"
            :loading="isProcessingSDN"
            :disabled="invalid"
            loadingIconColor="black"
            @click="saveSDNs"
            type="submit"
          />
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { EventBus } from "@/common/eventbus.js";
import { dereference } from "@/common/transforms";
import { sdnTemplateToSubmission } from "@/common/sdn";
import { handleValidationError } from "@/common/utils";
import { APPLICATION_MODULE } from "@/store/types/module-types";

import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton.vue";
import BaseTextarea from "@/components/BaseComponents/BaseTextarea.vue";
import BaseAlert from "@/components/BaseComponents/BaseAlert.vue";

export default {
  components: {
    BaseButton,
    BaseToggleButton,
    BaseTextarea,
    BaseAlert,
  },
  props: {
    value: Boolean,
    sdnData: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      sdnTemplate: dereference(this.sdnData),
      valueLocal: this.value,
      navDrawerContent: null,
      showToggles: Boolean,
      isRequestError: false,
      isProcessingSDN: false,
    };
  },
  computed: {
    sdnSubmission() {
      return sdnTemplateToSubmission(this.sdnTemplate);
    },
    invalid() {
      return !this.sdnSubmission?.Sections?.length;
    },
  },
  watch: {
    value(val) {
      this.valueLocal = val;
    },
    valueLocal(val) {
      this.$emit("input", val);

      // when the drawer opens/closes we set the scroll position to the top
      // the second arg is the target element
      // without this Vuetify will default to the page rather than the element
      this.$vuetify.goTo(0, { container: this.navDrawerContent });
    },
  },
  created() {
    // Listen for validation errors globally
    EventBus.$on("validation-error", handleValidationError);
  },
  mounted() {
    // set which element we want to control scrolling
    this.navDrawerContent =
      this.$refs.customiseDemandsAndNeeds.$el.querySelector(
        "div.v-navigation-drawer__content"
      );
  },
  methods: {
    ...mapActions(APPLICATION_MODULE, ["handleSaveSDNs"]),
    populateSelectedSDNs(sdnTemplate) {
      this.isRequestError = false;
      for (let section of sdnTemplate) {
        section.text = "";

        const selectedItems = section.sdnItems.filter((si) => si.value);
        if (!selectedItems) continue;

        selectedItems.forEach((si) => (section.text += `${si.text}.\n`));
      }

      this.showToggles = !this.showToggles;
      if (this.invalid) {
        setTimeout(() => {
          EventBus.$emit("validation-error", "Validation error occurred");
        }, 100);
      }
    },
    saveSDNs() {
      this.isRequestError = false;
      this.isProcessingSDN = true;

      this.handleSaveSDNs(this.sdnSubmission)
        .then(() => {
          this.isProcessingSDN = false;
          this.valueLocal = false;
          this.$emit("refreshDocs");
          this.sdnTemplate = dereference(this.sdnData);
          this.showToggles = !this.showToggles;
        })
        .catch((err) => {
          console.error(err);
          this.isRequestError = true;
          this.isProcessingSDN = false;
          this.$vuetify.goTo(0, { container: this.navDrawerContent });
          this.sdnTemplate = dereference(this.sdnData);
          this.showToggles = !this.showToggles;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/AppComponents/_DrawerCustomizeCover.scss";

.submit-btn.v-btn.v-btn--block.v-btn--disabled.v-btn--has-bg {
  background-color: $ps-orange !important;
  color: $black-2 !important;
  opacity: 0.5 !important;
  border: none !important;
}
</style>
